import { EventDTO } from '../../../../dto/event/event.dto';
import { EducationalPathUserDTO } from '../../../../dto/educational-path/educational-path-user.dto';
import { EducationalPathItemUserDTO } from '../../../../dto/educational-path/educational-path-item-user.dto';
import { ParticipationEventDTO } from '../../../../dto/event/participation-event.dto';
import { ModuleDTO } from '../../../../dto/module/module.dto';

export interface PathModuleCompletedOverlap {
  pathName: string;
  moduleId: number;
  moduleName: string;
  priorEventName?: string;
  priorEventDate?: string;
}

export interface PathModuleFutureRegistration {
  pathName: string;
  moduleId: number;
  moduleName: string;
  eventName: string;
  eventDate: string;
}

export interface PathModuleParticipationHints {
  completedOverlaps: PathModuleCompletedOverlap[];
  futureRegistrations: PathModuleFutureRegistration[];
}

export function getEventModuleIds(event: EventDTO): number[] {
  const ids = (event.items ?? [])
    .filter((item) => item.item_type === 'modules' && !item.deleted)
    .map((item) => item.item_id);
  return [...new Set(ids.filter((id) => Number.isFinite(id) && id > 0))];
}

export function getPathMaintenanceModuleIds(path: EducationalPathUserDTO): number[] {
  const items = path.maintenance ?? [];
  const ids: number[] = [];
  for (const item of items) {
    const mid = getPathItemModuleId(item);
    if (mid != null) {
      ids.push(mid);
    }
  }
  return [...new Set(ids)];
}

export function getPathItemModuleId(
  item: EducationalPathItemUserDTO | Record<string, unknown>
): number | null {
  const record = item as Record<string, unknown>;
  const itemDetails = record['item_details'] as Record<string, unknown> | undefined;
  const candidates = [
    record['item_id'],
    record['module_id'],
    itemDetails?.['module_id'],
    itemDetails?.['id'],
    record['id'],
  ];
  for (const raw of candidates) {
    if (raw == null || raw === '') {
      continue;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return null;
}

export function getParticipationEventModuleIds(event: ParticipationEventDTO): number[] {
  const raw = event.module_ids;
  if (!raw || !Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((id) => Number(id))
    .filter((n) => Number.isFinite(n) && n > 0);
}

export function normalizeParticipationStatus(status?: string): string {
  return (status ?? '').trim().toLowerCase();
}

export function isParticipationCompletedStatus(status: string): boolean {
  const s = normalizeParticipationStatus(status);
  return s === 'completed' || s === 'shownup' || s === 'attended';
}

export function isActiveRegistrationStatus(status: string): boolean {
  const s = normalizeParticipationStatus(status);
  return s === 'pending' || s === 'waitlist' || s === 'confirmed';
}

export function startOfLocalDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function isFutureEventDate(dateStr: string, now: Date): boolean {
  const parsed = Date.parse(dateStr);
  if (!Number.isFinite(parsed)) {
    return false;
  }
  return startOfLocalDay(new Date(parsed)).getTime() > startOfLocalDay(now).getTime();
}

export function isDateInRecurrenceWindow(
  dateStr: string,
  path: EducationalPathUserDTO
): boolean {
  const parsed = Date.parse(dateStr);
  if (!Number.isFinite(parsed)) {
    return false;
  }
  const t = parsed;
  const start = path.recurrence_window_start
    ? Date.parse(path.recurrence_window_start)
    : NaN;
  const end = path.recurrence_window_end
    ? Date.parse(path.recurrence_window_end)
    : NaN;
  if (Number.isFinite(start) && t < start) {
    return false;
  }
  if (Number.isFinite(end) && t > end) {
    return false;
  }
  return true;
}

export function isNowInRecurrenceWindow(
  path: EducationalPathUserDTO,
  now: Date
): boolean {
  const t = now.getTime();
  const start = path.recurrence_window_start
    ? Date.parse(path.recurrence_window_start)
    : NaN;
  const end = path.recurrence_window_end
    ? Date.parse(path.recurrence_window_end)
    : NaN;
  if (Number.isFinite(start) && t < start) {
    return false;
  }
  if (Number.isFinite(end) && t > end) {
    return false;
  }
  return true;
}

export function resolveModuleDisplayName(
  moduleId: number,
  event: EventDTO,
  pathMaintenanceItem?: EducationalPathItemUserDTO | Record<string, unknown>
): string {
  const fromPath = pathMaintenanceItem as Record<string, unknown> | undefined;
  const pathName = fromPath?.['name'];
  if (typeof pathName === 'string' && pathName.trim()) {
    return pathName.trim();
  }
  for (const item of event.items ?? []) {
    if (item.item_type !== 'modules' || item.item_id !== moduleId) {
      continue;
    }
    const details = item.item_details as ModuleDTO | undefined;
    if (details?.name?.trim()) {
      return details.name.trim();
    }
  }
  return `Modul #${moduleId}`;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function buildCompletedWarningHtml(
  overlaps: PathModuleCompletedOverlap[]
): string {
  if (!overlaps.length) {
    return '';
  }
  const items = overlaps.map((o) => {
    const module = escapeHtml(o.moduleName);
    const path = escapeHtml(o.pathName);
    let detail = `Du har allerede gennemført <strong>${module}</strong> på uddannelsesvejen <strong>${path}</strong> i den nuværende vedligeholdelsesperiode.`;
    if (o.priorEventName && o.priorEventDate) {
      const ev = escapeHtml(o.priorEventName);
      const date = escapeHtml(
        new Date(o.priorEventDate).toLocaleDateString('da-DK')
      );
      detail += ` Senest via eventet «${ev}» (${date}).`;
    }
    detail += ' Overvej om du vil deltage igen.';
    return `<li>${detail}</li>`;
  });
  return `<div class="participate-path-module-warning" role="status"><p><strong>Bemærk</strong></p><ul>${items.join('')}</ul></div>`;
}

export function resolveStatusYearForParticipate(
  event: EventDTO,
  now: Date
): number {
  const eventStart = event.start_date ? Date.parse(event.start_date) : NaN;
  if (Number.isFinite(eventStart)) {
    return new Date(eventStart).getFullYear();
  }
  return now.getFullYear();
}

export function analyzePathModuleParticipationHints(
  event: EventDTO,
  paths: EducationalPathUserDTO[],
  now: Date = new Date()
): PathModuleParticipationHints {
  const eventModuleIds = new Set(getEventModuleIds(event));
  if (eventModuleIds.size === 0 || !paths.length) {
    return { completedOverlaps: [], futureRegistrations: [] };
  }

  const currentEventGuid = (event.guid ?? '').trim().toLowerCase();
  const completedOverlaps: PathModuleCompletedOverlap[] = [];
  const futureRegistrations: PathModuleFutureRegistration[] = [];
  const completedKeys = new Set<string>();
  const futureKeys = new Set<string>();

  for (const path of paths) {
    if (!isNowInRecurrenceWindow(path, now)) {
      continue;
    }

    const pathName =
      path.full_name?.trim() || path.name?.trim() || 'Uddannelsesvej';
    const maintenanceItems = path.maintenance ?? [];
    const maintenanceByModule = new Map<number, EducationalPathItemUserDTO>();
    for (const item of maintenanceItems) {
      const mid = getPathItemModuleId(item);
      if (mid != null && eventModuleIds.has(mid)) {
        maintenanceByModule.set(mid, item as EducationalPathItemUserDTO);
      }
    }

    if (maintenanceByModule.size === 0) {
      continue;
    }

    for (const pe of path.participation_events ?? []) {
      if (!pe.date) {
        continue;
      }
      const peGuid = (pe.uuid ?? '').trim().toLowerCase();
      if (currentEventGuid && peGuid === currentEventGuid) {
        continue;
      }
      if (!isDateInRecurrenceWindow(pe.date, path)) {
        continue;
      }

      const peModules = getParticipationEventModuleIds(pe).filter((id) =>
        maintenanceByModule.has(id)
      );
      if (peModules.length === 0) {
        continue;
      }

      const status = normalizeParticipationStatus(pe.status);
      const future = isFutureEventDate(pe.date, now);

      for (const moduleId of peModules) {
        const moduleName = resolveModuleDisplayName(
          moduleId,
          event,
          maintenanceByModule.get(moduleId)
        );

        if (isParticipationCompletedStatus(status) && !future) {
          const key = `${path.path_id ?? pathName}:${moduleId}`;
          if (completedKeys.has(key)) {
            continue;
          }
          completedKeys.add(key);
          completedOverlaps.push({
            pathName,
            moduleId,
            moduleName,
            priorEventName: pe.name || undefined,
            priorEventDate: pe.date,
          });
          continue;
        }

        if (isActiveRegistrationStatus(status) && future) {
          const key = `${path.path_id ?? pathName}:${moduleId}:${peGuid}`;
          if (futureKeys.has(key)) {
            continue;
          }
          futureKeys.add(key);
          futureRegistrations.push({
            pathName,
            moduleId,
            moduleName,
            eventName: pe.name || 'Event',
            eventDate: pe.date,
          });
        }
      }
    }
  }

  return { completedOverlaps, futureRegistrations };
}
